import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'

import {
  Map,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  APILoadingStatus,
  useApiLoadingStatus,
  useMap,
} from '@vis.gl/react-google-maps'

import { Navigate } from 'react-router-dom'

import { Box } from '@mui/material'

import { useQueryStations } from '@/api/stations/queries'

import { Station } from '@/types/Station'

import { useBackButton } from '@/hooks/useBackButton'
import { useGeolocationContext } from '@/contexts/GeolocationContext'

import { ErrorBoundary } from '@/components/ErrorBoundary'

import { AppRoutes } from '@/routes/types/routes'

import SearchBar from '@/modules/Map/SearchBar'
import LocationFab from '@/modules/Map/LocationFab'

import { useKeyboard } from '@/contexts/KeyboardContext'
import { useStationModal } from '@/contexts/StationModalContext'

import { haversineDistance, sortStationsByDistance } from '@/utils/map'
import { chargingPointStatusUtils } from '@/utils/chargingPointStatus'
import { Position } from '@capacitor/geolocation'

declare global {
  interface Window {
    gm_authFailure?: () => void
  }
}

const MAP_UNAVAILABLE_ROUTE = `${AppRoutes.STATIONS}?mapUnavailable=true`

interface MapProps {
  initialLat?: number
  initialLng?: number
}

const MapPage: React.FC<MapProps> = ({ initialLat = -8.052207, initialLng = -34.949004 }) => {
  useBackButton()

  const map = useMap()
  const apiLoadingStatus = useApiLoadingStatus()
  const { isKeyboardVisible } = useKeyboard()
  const { openModal, closeModal } = useStationModal()

  const isProgrammaticallyMovingCamera = useRef(false)
  const hasAutoCenteredRef = useRef(false)

  const [visibleRadius, setVisibleRadius] = useState(0)

  const [selectedStation, setSelectedStation] = useState<Station | null>(null)
  const [isTrackingCurrentLocation, setIsTrackingCurrentLocation] = useState(false)

  const { currentLocation, coords, isPending, ensureLocation, triggerRequestLocation } = useGeolocationContext()

  const { data: stations, isLoading: isLoadingStations } = useQueryStations({
    infinite: true,
  })

  useEffect(() => {
    ensureLocation()
  }, [ensureLocation])

  useEffect(() => {
    if (!map || !coords || hasAutoCenteredRef.current) return

    hasAutoCenteredRef.current = true
    isProgrammaticallyMovingCamera.current = true
    map.moveCamera({ center: coords, zoom: 16 })
    isProgrammaticallyMovingCamera.current = false
    setIsTrackingCurrentLocation(true)
  }, [map, coords])

  const sortedStations = useMemo(() => {
    return sortStationsByDistance(stations?.results || [], coords)
  }, [stations?.results, coords])

  const handleOnClickCurrentLocationClick = async () => {
    if (isTrackingCurrentLocation) {
      setIsTrackingCurrentLocation(false)
      return
    }

    isProgrammaticallyMovingCamera.current = true

    const coordinates = await triggerRequestLocation()

    if (!coordinates) {
      isProgrammaticallyMovingCamera.current = false
      return
    }

    if (map) {
      map.moveCamera({
        center: {
          lat: coordinates.coords.latitude,
          lng: coordinates.coords.longitude,
        },
        zoom: 15,
      })
    }

    isProgrammaticallyMovingCamera.current = false
    setIsTrackingCurrentLocation(true)
  }

  const handleOnCenterChanged = () => {
    if (isProgrammaticallyMovingCamera.current) {
      return
    }

    setIsTrackingCurrentLocation(false)
  }

  const handleOnClickStation = (station: Station) => {
    map?.moveCamera({
      center: {
        lat: station.latitude,
        lng: station.longitude,
      },
      zoom: 16,
    })

    openModal(station, () => setSelectedStation(null))
    setSelectedStation(station)
  }

  const getAccuracyInPx = (currentLocation: Position | null): number => {
    if (!map || !currentLocation) {
      return 0
    }

    const accuracy = currentLocation.coords.accuracy
    const visibleRadiusInPx = (accuracy / visibleRadius) * window.innerHeight

    return visibleRadiusInPx
  }

  const getVisibleRadius = useCallback((): number => {
    if (!map) {
      return 0
    }

    const bounds = map.getBounds()

    if (!bounds) {
      return 0
    }

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()

    const topLeft = new window.google.maps.LatLng(ne.lat(), sw.lng())
    const bottomRight = new window.google.maps.LatLng(sw.lat(), ne.lng())

    const distance = haversineDistance(topLeft, bottomRight)

    return distance / 2
  }, [map])

  useEffect(() => {
    if (!selectedStation) {
      closeModal()
      return
    }

    handleOnClickStation(selectedStation)
  }, [selectedStation])

  useEffect(() => {
    if (!map) return

    const handleBoundsChanged = () => {
      const radius = getVisibleRadius()

      setVisibleRadius(radius)
    }

    const listener = map.addListener('bounds_changed', handleBoundsChanged)

    handleBoundsChanged()

    return () => {
      window.google.maps.event.removeListener(listener)
    }
  }, [map, getVisibleRadius])

  useEffect(() => {
    window.gm_authFailure = () => window.location.replace(MAP_UNAVAILABLE_ROUTE)

    return () => {
      window.gm_authFailure = undefined
    }
  }, [])

  if (apiLoadingStatus === APILoadingStatus.AUTH_FAILURE || apiLoadingStatus === APILoadingStatus.FAILED) {
    return <Navigate to={MAP_UNAVAILABLE_ROUTE} replace />
  }

  return (
    <ErrorBoundary fallback={<Navigate to={MAP_UNAVAILABLE_ROUTE} replace />}>
      <SearchBar
        stations={sortedStations}
        isLoadingStations={isLoadingStations}
        selectedStation={selectedStation}
        setSelectedStation={setSelectedStation}
        userCoords={coords}
      />
      {!isKeyboardVisible && !selectedStation ? (
        <LocationFab
          isTrackingCurrentLocation={isTrackingCurrentLocation}
          isGettingCurrentLocation={isPending}
          handleOnClickCurrentLocationClick={handleOnClickCurrentLocationClick}
        />
      ) : null}
      <Map
        mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID}
        defaultCenter={{ lat: initialLat, lng: initialLng }}
        defaultZoom={16}
        minZoom={4}
        maxZoom={20}
        reuseMaps={true}
        clickableIcons={false}
        disableDefaultUI={true}
        onCenterChanged={handleOnCenterChanged}
        style={{
          display: isKeyboardVisible ? 'none' : 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {coords ? (
          <AdvancedMarker position={coords} anchorPoint={AdvancedMarkerAnchorPoint.CENTER}>
            <Box
              width={16}
              height={16}
              bgcolor="#4285F4"
              borderRadius="50%"
              sx={{
                border: '2px solid #fff',
                outline: `${getAccuracyInPx(currentLocation)}px solid #1bb6ff55`,
              }}
            />
          </AdvancedMarker>
        ) : null}
        {sortedStations.map((station) => {
          const iconPath = chargingPointStatusUtils.getStationPinIcon(station.status)

          return (
            <AdvancedMarker
              key={station.id}
              position={{
                lat: station.latitude,
                lng: station.longitude,
              }}
              anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
              onClick={() => handleOnClickStation(station)}
            >
              <Box component="img" src={iconPath} width={42} height={42} />
            </AdvancedMarker>
          )
        })}
      </Map>
    </ErrorBoundary>
  )
}

export default MapPage
